import { useState, useEffect, useCallback } from "react";

export interface Product {
  id: number;
  name: string;
  rValue: string;
  size: string;
  coverage: string;
  price: string;
  qty: number;
  inStock: boolean;
}

const EDGE_FUNCTION_URL =
  "https://aqjwgsbonhnionffftcc.supabase.co/functions/v1/get-inventory";

export function useInventory() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(EDGE_FUNCTION_URL);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      if (data.products) {
        setProducts(data.products);
        setLastUpdated(new Date());
      }
    } catch {
      setError("Could not load live inventory. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
    const interval = setInterval(fetchInventory, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchInventory]);

  // Extract unique in-stock R-values from product names (e.g. "R13" → "R-13")
  const inStockRValues = Array.from(
    new Set(
      products
        .filter((p) => p.inStock && /R\d+/i.test(p.name))
        .map((p) => {
          const match = p.name.match(/R(\d+)/i);
          return match ? `R-${match[1]}` : null;
        })
        .filter(Boolean) as string[]
    )
  ).sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ""), 10);
    const numB = parseInt(b.replace(/\D/g, ""), 10);
    return numA - numB;
  });

  return { products, loading, error, lastUpdated, fetchInventory, inStockRValues };
}
